/**
 * AppShell — the One Compass global chrome (v2.0).
 *
 * Ships the persistent deep-teal left rail from the shell prototype
 * (docs/design/prototypes/_shell.css + _shell.js): brand, the seven adopted
 * top-level destinations (v2.0-settings-ia-regrouping.md Decision 1 +
 * v2.0-readiness-dashboard-consolidation.md), and a footer carrying the
 * signed-in user + version.
 *
 * Destination → route mapping (DC-1645 IA regrouping):
 *   Home        /home
 *   Journeys    /journeys (+ active for /journey/*, /analysis/*, /import/*)
 *   Readiness   /readiness (DC-1646 — Data lens = Triangulation domain list;
 *               Organizational lens = the Execution Risk dashboard's
 *               canonical home; /triangulation/:domainId stays the domain zoom)
 *   Ontology    /ontology (Private Ontologies — the ONLY access point since
 *               DC-1645; removed from Settings per operator decision 6/11)
 *   People      /settings/team (Members | FAIR Personas | Interviews)
 *   Governance  /settings/governance (Dashboard | Documents | Standards |
 *               Recommendations | Evidence Library)
 *   Settings    /settings (Domains | Integrations | AI Preferences +
 *               Access & Security admin cluster)
 *
 * The rail hides itself on unauthenticated/auth-flow routes and the external
 * vendor/portal surfaces.
 */

import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useExperienceProfile } from '../../hooks/useExperienceProfile';
import { getNavDescriptors, type NavDescriptor } from '../../config/experienceProfiles';
import { WorkflowChangeNotice } from './WorkflowChangeNotice';
import { APP_VERSION_LABEL } from '../../config/appVersion';
import '../../styles/app-shell.css';

// Routes that render WITHOUT the shell chrome.
const SHELL_FREE_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/accept-invitation',
  '/access-denied',
  '/portal',
  '/vendors',
  '/vendor',
];

const stroke = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const profile = useExperienceProfile();

  // Profile-keyed navigation (design section 5.7). While resolution is
  // pending or the profile is unresolvable/disabled, the rail renders NO
  // profile-derived destinations -- a wrong-product flash is a defect.
  const dests: NavDescriptor[] =
    profile.status === 'resolved'
      ? getNavDescriptors(profile.definition.key, profile.definition.version)
      : [];

  const shellFree =
    !user || SHELL_FREE_PREFIXES.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));

  if (shellFree) {
    return <>{children}</>;
  }

  const initials = (user.name || user.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase())
    .join('');

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-shell" data-testid="app-shell">
      <aside className="shell-rail" aria-label="Primary">
        {/* The brand returns to '/', where ProfileLanding resolves the
            organization's own entry — Overview for banking, Home for the
            dataset profile. Never a hardcoded surface. */}
        <Link to="/" className="rail-brand" aria-label="Go to the home page">
          <svg viewBox="0 0 24 24" {...stroke} aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="m15.5 8.5-2.1 4.9-4.9 2.1 2.1-4.9Z" />
          </svg>
          <span className="rail-brand-name">
            <small>DATA</small>
            <b>Compass</b>
          </span>
        </Link>
        {profile.status === 'resolved' &&
          profile.definition.identity === 'banking_use_case_v1' && (
            <span className="rail-lens" data-testid="rail-lens">
              Banking · AI Risk &amp; Compliance
            </span>
          )}

        <nav className="rail-nav">
          {dests.map((d) =>
            d.to === null ? (
              // A named stage the product has not built. Rendered, because the
              // sequence is part of how the product explains itself — but not
              // a link, because a rail entry that 404s is worse than no entry
              // (D-BANK-UX-03).
              <span
                key={d.key}
                className="rail-item is-pending"
                data-testid={`rail-${d.key}`}
                aria-disabled="true"
              >
                {d.icon}
                <span>{d.label}</span>
                <em className="rail-item-soon">In development</em>
              </span>
            ) : (
              <NavLink
                key={d.key}
                to={d.to}
                className={d.isActive(location.pathname, location.search) ? 'rail-item is-active' : 'rail-item'}
                data-testid={`rail-${d.key}`}
              >
                {d.icon}
                <span>{d.label}</span>
              </NavLink>
            )
          )}
        </nav>

        <div className="rail-foot">
          <div className="rail-user">
            <span className="rail-user-avatar" aria-hidden="true">
              {initials}
            </span>
            <span className="rail-user-name">{user.name || user.email}</span>
          </div>
          <div className="rail-foot-links">
            <button type="button" className="rail-link" onClick={() => navigate('/profile')}>
              Profile
            </button>
            <button type="button" className="rail-link" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
          <span className="rail-ver">{APP_VERSION_LABEL}</span>
        </div>
      </aside>

      <div className="shell-main">
        {/* A workflow change explains itself to the members who did not make
            it, on whatever surface they land on (DC-1802). */}
        <WorkflowChangeNotice />
        {children}
      </div>
    </div>
  );
};

export default AppShell;
